import React from "react";
import PropTypes from "prop-types";
import { Button } from "@vitalsource/vstui";
import { cloud } from "@vitalsource/vstui/colors";
import { CaretDownLarge, CaretUpLarge } from "@vitalsource/vstui-icons";
import styled, { withTheme } from "styled-components";
import BookStore from "../../../stores/book-store";
import Dispatcher from "../../../dispatcher";
import { removeDuplicates } from "../../../utils/array-utils";
import executePrintJob from "./execute-print-job";

const Container = styled.div`
  padding-left: 16px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow: auto;
  position: relative;
  transition: max-height 0.5s ease;
  max-height: ${p => (p.expanded ? "210px" : "0px")};
`;

const Item = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  height: 44px;
  font-size: 1.6rem;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  border-bottom: 1px solid ${cloud.getShade(4)};
`;

const CentralizedItem = styled(Item)`
  justify-content: center;
`;

const Header = styled.button`
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.6rem;
  font-weight: 400;
  line-height: 1.5;
  border: none;
  width: 100%;
  border-bottom: 1px solid ${cloud.getShade(4)};
`;

const TitleWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
`;

const Title = styled.div`
  margin-left: 10px;
`;

const SpinnerStyle = {
  width: 32,
  height: 32
};

class PrintHistory extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      loading: true,
      data: [],
      pageSize: 1000,
      page: 1,
      pages: 1,
      total: 0
    };

    this.fetchHistory = this.fetchHistory.bind(this);
    this.toggleList = this.toggleList.bind(this);
    this.handlePrintDone = this.handlePrintDone.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    this.fetchHistory({ page: 1, pageSize: this.state.pageSize });
    Dispatcher.bind(Dispatcher.PRINT_DONE, this.handlePrintDone);
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.PRINT_DONE, this.handlePrintDone);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.setState({ expanded: this.props.expanded || false });
    }
  }

  handlePrintDone() {
    // Let's use pageSize=10 to avoid race conditions.
    // FetchHistory will remove duplicates anyway.
    this.fetchHistory({ page: 1, pageSize: 10 }, false);
  }

  toggleList() {
    this.setState(({ expanded }) => ({ expanded: !expanded }));
  }

  fetchHistory(params, insertLast = true) {
    this.setState({ loading: true });
    const book = BookStore.get(this.props.isbn);
    const onSuccess = ({ requests, total, page, pages }) => {
      this.setState(state => {
        let data = insertLast
          ? state.data.concat(requests)
          : requests.concat(state.data);

        data = removeDuplicates(data, record => record.reqid);
        data = removeDuplicates(data, record => `${record.from}-${record.to}`);

        return { data, total, page, pages, loading: false };
      });
    };

    book.viewer.Book.getPrintHistory(params, response => {
      if (response.isSuccess()) {
        onSuccess(response.data);
      } else {
        this.setState({ loading: false });
      }
    });
  }

  handleScroll = event => {
    if (!this.state.loading && this.state.page < this.state.pages) {
      const list = event.target;
      const lastItem = list.children[list.childElementCount - 1];
      const lastItemPosition = lastItem.offsetTop;
      const currentScrollPosition = list.scrollTop + list.offsetHeight;
      if (currentScrollPosition >= lastItemPosition) {
        this.fetchHistory({
          page: this.state.page + 1,
          pageSize: this.state.pageSize
        });
      }
    }
  };

  renderHistory() {
    const { loading, data, expanded } = this.state;
    return (
      <List expanded={expanded} onScroll={this.handleScroll}>
        {data.map(request => (
          <Item key={request.reqid}>
            <div>
              {request.from === request.to
                ? i18n.t("print.page", request)
                : i18n.t("print.pages", request)}
            </div>
            <Button
              onClick={() =>
                executePrintJob(
                  this.props.theme,
                  null,
                  this.props.isbn,
                  request.from,
                  request.to
                )
              }
              inline
            >
              {i18n.t("print.print")}
            </Button>
          </Item>
        ))}
        {loading && (
          <CentralizedItem>
            <div className="spinning" style={SpinnerStyle} />
          </CentralizedItem>
        )}
      </List>
    );
  }

  render() {
    const { theme } = this.props;
    const { expanded, total } = this.state;
    const ArrowIcon = expanded ? CaretUpLarge : CaretDownLarge;

    if (total > 0) {
      return (
        <Container>
          <Header onClick={this.toggleList} aria-expanded={expanded}>
            <TitleWrapper>
              <ArrowIcon color={theme.vstui.color.primary.getShade(6)} />
              <Title>{i18n.t("print.print-history")}</Title>
            </TitleWrapper>
          </Header>
          {this.renderHistory()}
        </Container>
      );
    }
    return null;
  }
}

PrintHistory.propTypes = {
  isbn: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  expanded: PropTypes.bool,
  theme: PropTypes.object
};

export default withTheme(PrintHistory);
